import classNames from 'clsx';
import React, { useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import type { Currency } from '@swf/core/enums';
import { Size } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import { select } from '@swf/core/store';
import type { ClubRecentTournPlayerResult } from '@swf/generated/types/api/v0/SwHomeGames';
import { FormattedCurrencyAmount, Table } from '@swf/poker';

import {
  TXTCLI_KO__tab,
  TXTCLI_Place,
  TXTCLI_Player,
  TXTCLI_Points,
  TXTCLI_Prize
} from './ResultsTable.messages.json';
import styles from './ResultsTable.scss';
import Badge from './assets/badge.svg';

interface Props {
  isPlayMoney?: boolean;
  results: ClubRecentTournPlayerResult[];
  currency: string;
}

interface ResultsData {
  [key: string]: number;
}

const ResultsTable: React.FC<Props> = ({ results, currency, isPlayMoney = false }) => {
  const user = useSelector(select.account.getUserId);
  const { greaterThan } = useScreenSize();
  const isMobile = !greaterThan(Size.XXSmall);

  const prizeColumn = {
    Header: (
      <>
        <Translation {...TXTCLI_Prize} /> {!isPlayMoney ? `(${currency})` : null}
      </>
    ),
    accessor: 'prize'
  };
  const placeColumn = { Header: <Translation {...TXTCLI_Place} />, accessor: 'place' };

  const columns = isMobile
    ? // eslint-disable-next-line react/jsx-no-useless-fragment
      [placeColumn, { Header: <></>, accessor: 'userId' }, prizeColumn]
    : [
        placeColumn,
        { Header: <Translation {...TXTCLI_Player} />, accessor: 'userId' },
        { Header: <Translation {...TXTCLI_Points} />, accessor: 'pointsEarned' },
        { Header: <Translation {...TXTCLI_KO__tab} />, accessor: 'knockouts' },
        prizeColumn
      ];

  const data: unknown = useMemo(
    () =>
      results.map(({ place, prize, pointsEarned, knockouts, scalePM, ...rest }) => ({
        place: (
          <div className={styles.place}>
            {place}
            <Badge
              className={classNames({
                gold: place === 1,
                silver: place === 2,
                bronze: place === 3
              })}
            />
          </div>
        ),
        prize: (
          <FormattedCurrencyAmount
            amount={prize}
            scalePM={scalePM}
            isPlayMoney={isPlayMoney}
            currency={currency as Currency}
          />
        ),
        pointsEarned: (
          <FormattedNumber
            value={Number(pointsEarned) / 1000}
            style="decimal"
            minimumFractionDigits={Number(pointsEarned) % 1 !== 0 ? 2 : 0}
          />
        ),
        knockouts: (
          <FormattedNumber
            value={Number(knockouts) / 1000}
            style="decimal"
            maximumFractionDigits={0}
          />
        ),
        ...rest
      })),
    [currency, results, isPlayMoney]
  );

  return (
    <div className={styles.container}>
      <Table
        columns={columns}
        data={data as ResultsData[]}
        rowClassName={({ userId }) => {
          if (userId.toString() === user) {
            return styles.currentUser;
          }
        }}
      />
    </div>
  );
};

export default ResultsTable;
